import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { MainContainerComponent } from 'src/containers/main/components/mainContainer.component';
import { UserIsLockedComponent } from 'src/containers/login/components/userIsLocked.component';

/**
 * These routes are used to check if the current route can
 * be activated for USA contracts.
 *
 * This should be done either with a canActivate guard, and/or
 * with `data` inside each route in {@link routes}.
 *
 * For example:
 *
 * ```
 * children: [
 *    {
 *       path: 'mercados',
 *       data: { isAvailableToUsa: true }
 *     },
 *     {
 *       path: 'funds',
 *       data: { isAvailableToUsa: false }
 *     },
 *   ]
 * ```
 *
 * But `data` is not being read successfully on route changes, and
 * we don't know of ways to trigger `canActivate` guards at will from
 * components.
 *
 * So we create a list of routes that a USA contract CAN access, and
 * use it to filter every visited route in
 * @see {@link MainContainerComponent.checkIfRouteCanBeActivated } .
 */
export const RoutesAvailableForUsa = [
  '/main/portfolio',
  '/main/mercados',
  '/main/ordenes/historial',
  '/main/herramientas/rendimientos',
];

export const routes: Routes = [
  { path: '', redirectTo: 'main/portfolio', pathMatch: 'full' },
  { path: 'main', redirectTo: 'main/portfolio', pathMatch: 'full' },
  {
    path: 'user-is-locked',
    component: UserIsLockedComponent,
  },
  {
    path: 'login',
    redirectTo: 'main/portfolio',
    pathMatch: 'full',
  },
  {
    path: 'main',
    component: MainContainerComponent,
    children: [
      {
        path: 'portfolio',
        loadChildren:
          () => import('./../containers/subContainers/portfolio/portfolio.module').then(m => m.PortfolioModule),
      },
      {
        path: 'mercados',
        loadChildren:
          () => import('./../containers/subContainers/operation/operation.module').then(m => m.OperationModule),
      },
      {
        path: 'news',
        loadChildren:
          () => import('./../containers/subContainers/news/news.module').then(m => m.NewsModule),
      },
      {
        path: 'funds',
        loadChildren:
          () => import('./../containers/subContainers/funds/funds.module').then(m => m.FundsModule),
      },
      {
        path: 'analisis',
        loadChildren:
          () => import('./../containers/subContainers/analysis/analysis.module').then(m => m.AnalysisModule),
      },
      {
        path: 'ordenes',
        loadChildren:
          () => import('./../containers/subContainers/orders/orders.module').then(m => m.OrdersModule),
      },
      {
        path: 'herramientas',
        loadChildren:
          () => import('./../containers/subContainers/tools/tools.module').then(m => m.ToolsModule),
      },
      {
        path: 'documentos',
        loadChildren:
          () => import('./../containers/subContainers/documents/documents.module').then(m => m.DocumentsModule),
      },
      {
        path: 'cuenta-bancaria',
        loadChildren:
          () => import('./../containers/subContainers/generalInformation/generalInformation.module').then(m => m.GeneralInformationModule),
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'main/portfolio',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { relativeLinkResolution: 'legacy' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
